/**
 * MODULE: domain/sales-presentations/frontend/components/SlideThumbnailStrip/SlideCanvasThumbnail
 * PURPOSE: Read-only miniature of a slide using the same CanvasSlideRenderer as
 *   the main editor (Epic 3 / E3-8). Inner canvas is pointer-events: none; the
 *   outer control handles clicks. Visual scale uses CSS transform (scale factor
 *   ≈ thumbnail width / 960). Slide prop updates are debounced 300ms so rapid
 *   keystrokes do not re-rasterize on every frame.
 * DEPENDENCIES: react, CanvasSlideRenderer, RenderedSlide
 * PUBLIC_API: SlideCanvasThumbnail, SlideCanvasThumbnailProps
 * SIDE_EFFECTS: canvas paint inside CanvasSlideRenderer
 * AGENT_SAFETY: 🟢 SAFE
 * FILE_SIZE: ~95 / 200
 */

import { useEffect, useState, useMemo } from 'react';
import type { RenderedSlide } from '../../api/types';
import { CanvasSlideRenderer } from '../CanvasSlideRenderer';

/** Debounce delay before thumbnail canvas catches up to slide edits (ms). */
export const THUMBNAIL_DEBOUNCE_MS = 300;

const BASE_W = 960;
const BASE_H = 540;
/** Spec example: thumbnail width / slide canvas width (~0.18). */
export const THUMB_SCALE = 0.18;

export interface SlideCanvasThumbnailProps {
  slide: RenderedSlide;
  isActive: boolean;
  slideNumber: number;
  onSelect: () => void;
  /** E5-5: high drop-off or low dwell — amber indicator (banner may be dismissed). */
  engagementWarning?: boolean;
  /**
   * E6-6: `ring` keeps the legacy full purple frame when active; `none` lets the
   * parent strip draw a primary left bar only.
   */
  activeFrame?: 'ring' | 'none';
}

/** Stable string for tests: first text layer value + layer count. */
export function thumbnailRevisionKey(slide: RenderedSlide): string {
  const layers = slide.layers ?? [];
  const firstText = layers.find((l) => l.type === 'text' && l.content.kind === 'text');
  const v =
    firstText && firstText.content.kind === 'text' ? firstText.content.value : '';
  return `${layers.length}:${v}`;
}

export function SlideCanvasThumbnail({
  slide,
  isActive,
  slideNumber,
  onSelect,
  engagementWarning = false,
  activeFrame = 'ring',
}: SlideCanvasThumbnailProps) {
  const [debouncedSlide, setDebouncedSlide] = useState(slide);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSlide(slide), THUMBNAIL_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [slide]);

  const scaledW = BASE_W * THUMB_SCALE;
  const scaledH = BASE_H * THUMB_SCALE;
  const slideRecord = useMemo(() => ({ ...debouncedSlide }) as unknown as Record<string, unknown>, [debouncedSlide]);

  return (
    <div
      data-testid={`slide-canvas-thumb-${slide.id}`}
      className={`relative w-full shrink-0 overflow-hidden rounded-md border-2 transition-colors ${
        isActive
          ? activeFrame === 'ring'
            ? 'border-[var(--color-primary,#7c3aed)] shadow-sm'
            : 'border-transparent shadow-sm'
          : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
      }`}
    >
      <button
        type="button"
        onClick={onSelect}
        className="relative block w-full cursor-pointer bg-gray-50 text-left dark:bg-gray-900"
        aria-current={isActive ? 'true' : undefined}
        aria-label={`Slide ${slideNumber}`}
      >
        <span
          className="absolute left-1 top-1 z-10 rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold text-white"
          aria-hidden
        >
          {slideNumber}
        </span>
        {engagementWarning ? (
          <span
            data-testid="slide-thumb-engagement-dot"
            className="absolute right-1 top-1 z-10 h-2 w-2 rounded-full ring-2 ring-white dark:ring-gray-900"
            style={{ backgroundColor: 'var(--color-warning, #f59e0b)' }}
            title="High drop-off on this slide — see suggestions"
          />
        ) : null}
        <div
          className="pointer-events-none relative mx-auto flex justify-center overflow-hidden"
          style={{ width: scaledW, height: scaledH }}
        >
          <div
            className="pointer-events-none absolute left-0 top-0"
            style={{
              width: BASE_W,
              height: BASE_H,
              transform: `scale(${THUMB_SCALE})`,
              transformOrigin: 'top left',
            }}
          >
            <CanvasSlideRenderer slide={slideRecord} width={BASE_W} height={BASE_H} scale={1} />
          </div>
        </div>
        <span
          data-testid="slide-thumb-revision"
          data-revision={thumbnailRevisionKey(debouncedSlide)}
          className="hidden"
          aria-hidden
        />
      </button>
    </div>
  );
}
